import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { translateAuthError } from '../../utils/authErrors';
import PasswordInput from './PasswordInput';
import SpamFolderHint from './SpamFolderHint';
import './AuthForm.css';

const LoginForm = ({ onSuccess, onSwitchToSignup }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetMode, setResetMode] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [unverified, setUnverified] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSent, setResendSent] = useState(false);
  const { login, logout, resetPassword, resendVerificationEmail } = useAuth();
  const toast = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setUnverified(false);
    setResendSent(false);
    setLoading(true);
    try {
      const result = await login(email, password);
      if (!result.user.emailVerified) {
        setUnverified(true);
        setLoading(false);
        return;
      }
      toast.success('Bem-vindo de volta!');
      onSuccess();
    } catch (err) {
      setError(translateAuthError(err));
    }
    setLoading(false);
  };

  const handleResendVerification = async () => {
    setResendLoading(true);
    setError('');
    try {
      await resendVerificationEmail();
      setResendSent(true);
      toast.success('Email de verificação reenviado!');
    } catch (err) {
      setError(translateAuthError(err, 'Não foi possível reenviar o email.'));
    }
    setResendLoading(false);
  };

  const handleBackToLogin = async () => {
    await logout();
    setUnverified(false);
    setResendSent(false);
    setError('');
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setError('');
    setResetLoading(true);
    try {
      await resetPassword(resetEmail);
      setResetSent(true);
      toast.success('Email enviado! Verifique contato@hakacompany.com.br (e a pasta spam).');
    } catch (err) {
      setError(translateAuthError(err));
    }
    setResetLoading(false);
  };

  if (resetMode) {
    return (
      <form onSubmit={handleReset} className="auth-form">
        <h2 className="auth-form-title">Recuperar Senha</h2>
        {error && <div className="auth-form-error">{error}</div>}
        {resetSent ? (
          <div className="auth-form-success">
            Email enviado para <strong>{resetEmail}</strong>!
            Procure um email de <strong>contato@hakacompany.com.br</strong> na caixa de entrada e no spam.
          </div>
        ) : (
          <>
            <p className="auth-form-hint">
              Digite seu email para receber um link de redefinição de senha.
              O email virá de <strong>contato@hakacompany.com.br</strong>.
            </p>
            <div className="auth-form-field">
              <input
                type="email"
                placeholder="Email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                required
                className="auth-form-input"
              />
            </div>
            <button type="submit" disabled={resetLoading} className="btn btn-filled auth-form-submit">
              <span>{resetLoading ? 'Enviando...' : 'Enviar link'}</span>
            </button>
          </>
        )}
        <div className="auth-form-switch">
          <button
            type="button"
            onClick={() => { setResetMode(false); setError(''); setResetSent(false); }}
            className="auth-form-link"
          >
            Voltar ao login
          </button>
        </div>
      </form>
    );
  }

  if (unverified) {
    return (
      <div className="auth-form auth-verification">
        <div className="auth-verification-icon" aria-hidden="true">✉️</div>
        <h2 className="auth-form-title">Confirme seu email para entrar</h2>
        <p className="auth-verification-text">
          Sua conta ainda não foi ativada. Verifique o email enviado para:
        </p>
        <p className="auth-verification-email">{email}</p>
        <p className="auth-form-hint auth-verification-hint">
          Clique no link de verificação antes de fazer login. Você só terá acesso
          à loja após confirmar o email.
        </p>
        <p className="auth-form-hint auth-verification-hint">
          Não recebeu? Clique em <strong>Reenviar</strong> — o email virá de{' '}
          <strong>contato@hakacompany.com.br</strong>.
        </p>
        <SpamFolderHint />

        {resendSent && (
          <div className="auth-form-success">Email de verificação reenviado!</div>
        )}
        {error && <div className="auth-form-error">{error}</div>}

        <button
          type="button"
          onClick={handleResendVerification}
          disabled={resendLoading}
          className="btn btn-outline auth-verification-resend"
        >
          <span>{resendLoading ? 'Reenviando...' : 'Reenviar email de verificação'}</span>
        </button>

        <button
          type="button"
          onClick={handleBackToLogin}
          className="btn btn-filled auth-form-submit"
        >
          <span>Voltar ao login</span>
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="auth-form">
      <h2 className="auth-form-title">Entrar</h2>
      {error && <div className="auth-form-error">{error}</div>}
      <div className="auth-form-field">
        <input
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          className="auth-form-input"
        />
      </div>
      <div className="auth-form-field">
        <PasswordInput
          placeholder="Senha"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          minLength={6}
          autoComplete="current-password"
        />
      </div>
      <div className="auth-form-forgot">
        <button
          type="button"
          onClick={() => { setResetMode(true); setError(''); setResetEmail(email); }}
          className="auth-form-link"
        >
          Esqueceu a senha?
        </button>
      </div>
      <button type="submit" disabled={loading} className="btn btn-filled auth-form-submit">
        <span>{loading ? 'Carregando...' : 'Entrar'}</span>
      </button>
      <div className="auth-form-switch">
        <span>Não tem uma conta? </span>
        <button type="button" onClick={onSwitchToSignup} className="auth-form-link">
          Criar conta
        </button>
      </div>
    </form>
  );
};

export default LoginForm;
